import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import isEqual from "lodash.isequal";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { addBidChoiceEvent, fetchLatestBidChoiceEventByReferenceNumberAndEid } from "../../api/bid.js";
import { fetchCurrentEmployeeMonthlyScheduleMap } from "../../api/scheduleEditor.js";
import PageLoading from "../../components/PageLoading.jsx";
import BidChoicesTable from "../../components/bid/BidChoicesTable.jsx";
import BidTimer from "../../components/bid/BidTimer.jsx";
import BidInfoDialog from "../../components/fundamentals/BidInfoDialog.jsx";
import ConfirmButton from "../../components/fundamentals/ConfirmButton.jsx";
import ConfirmationDialog from "../../components/fundamentals/ConfirmationDialog.jsx";
import SlideUpTransition from "../../components/fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs.jsx";
import SuccessDialog from "../../components/fundamentals/SuccessDialog.jsx";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { BID_STATUS, BID_TYPE, EMPLOYEE_BID_STATUS } from "../../util/bid/bidConstants.js";
import { buildBidChoiceEvent, prepareNewBidChoiceEventPayload } from "../../util/bid/bidEventBuilder.js";
import { getScrubbedBidEvent } from "../../util/bid/bidScrubber.js";
import { getBidName, getDisabledDatesBasedOnSchedule, getNonEmptyChoices } from "../../util/bid/timeOffBidUtils.js";
import { getEidFromHref, getReferenceNumberFromHref } from "../../util/bid/shiftBidUtils.js";
import { BREADCRUMBS_TITLE, CANCEL_WARNING_MESSAGE_ACTION_TYPE, FEATURES, HOME_PATH } from "../../util/constants.js";
import { convertMonthStringToInteger } from "../../util/dateTimeFormatter.js";
import {
  BID_CHOICE_SAVE_ERROR_MESSAGE,
  BID_FORM_CANCEL_MESSAGE,
  BID_FORM_CLEAR_ALL,
  BID_FORM_CLEAR_ALL_CHOICES_MESSAGE,
  FTO_BID_CHOICE_CONFIRMATION_MESSAGE,
  FTO_BID_CHOICE_CONFIRMATION_TITLE,
  LAST_UPDATED,
  LAST_UPDATED_TIMESTAMP,
} from "../../util/messageConstants.js";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService.js";
import { validateFTOCount } from "../../util/bid/bidInputValidation.js";
import IAMFTOBidPDFExport from "./IAMFTOBidPDFExport.jsx";

const useStyles = makeStyles((theme) => ({
  lastSubmitted: {
    textAlign: "left",
    color: theme.palette.secondary.swaGray4,
    marginRight: "3.5vw",
  },
  addChoice: {
    margin: "15px",
  },
  inputText: {
    width: "110px",
    textAlign: "left",
    paddingRight: "10px",
    "& .MuiInputBase-root": {
      background: "white",
    },
  },
  bidInput: {
    paddingLeft: "10px",
  },
}));

const IAMFTOBidChoices = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isBidChoicesLoading, setIsBidChoicesLoading] = useState(true);
  // Bid Choice Data State
  const [bidChoiceData, setBidChoiceData] = useState(null);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [ftoCount, setFtoCount] = useState("");
  const [ftoCountErrors, setFtoCountErrors] = useState("");
  const [initialSelectedChoices, setInitialSelectedChoices] = useState([]);
  const [initialFTOCount, setInitialFTOCount] = useState("");

  // Dialog state
  const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isGuideDialogOpen, setIsGuideDialogOpen] = useState(false);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isSubmitResponseDialogOpen, setIsSubmitResponseDialogOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [submitErrors, setSubmitErrors] = useState("");

  const [lastUpdatedByFirstName, setLastUpdatedByFirstName] = useState("");
  const [lastUpdatedByLastName, setLastUpdatedByLastName] = useState("");

  // Schedule state
  const [disabledDates, setDisabledDates] = useState([]);
  const [isScheduleLoading, setIsScheduleLoading] = useState(true);

  const [isReadOnly, setIsReadOnly] = useState(false);

  useEffect(() => {
    fetchLatestFTOBidChoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDesktopView]);

  useEffect(() => {
    if (bidChoiceData) {
      fetchSchedule(bidChoiceData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidChoiceData]);

  useEffect(() => {
    // validate choices when count is present
    if (ftoCount) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChoices]);

  const handleFTOCountEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleFTOCountBlur(event);
    }
  };

  const handleFTOCountChange = (event) => {
    const input = event.target.value;
    const sanitizedInput = input.replace(/[^0-9]/g, "");
    setFtoCount(sanitizedInput);
    setFtoCountErrors("");
  };

  const handleFTOCountBlur = () => {
    const numOfChoices = getNonEmptyChoices(selectedChoices)?.length;
    const errors = validateFTOCount(ftoCount, numOfChoices);

    if (errors === "") {
      setFtoCountErrors("");
    } else {
      setFtoCountErrors(errors);
    }
  };

  let history = useHistory();

  const fetchSchedule = ({ eid, station, schedYear, schedMonth }) => {
    if (eid && station && schedYear && schedMonth) {
      setIsScheduleLoading(true);
      fetchCurrentEmployeeMonthlyScheduleMap(eid, station, schedYear, convertMonthStringToInteger(schedMonth))
        .then((res) => res.json())
        .then((response) => {
          if (response?.event) {
            const disabledDates = getDisabledDatesBasedOnSchedule({
              scheduledMap: response.event,
              station: station,
              bidType: BID_TYPE.FTO,
              schedMonth: schedMonth,
              schedYear: schedYear,
            });
            setDisabledDates(disabledDates);
            setIsScheduleLoading(false);
          } else {
            throw Error();
          }
        })
        .catch((err) => {
          setIsScheduleLoading(false);
          console.error(`Bid Error: %j`, err);
        });
    }
  };

  const fetchLatestFTOBidChoices = () => {
    const eid = getEidFromHref();
    const referenceNumber = getReferenceNumberFromHref();
    fetchLatestBidChoiceEventByReferenceNumberAndEid(eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response?.event) {
          const bidChoiceEvent = getScrubbedBidEvent(response.event);
          setBidChoiceData(bidChoiceEvent);
          setSelectedChoices(response.event?.choices?.length > 0 ? response.event.choices : [""]);
          setInitialSelectedChoices(response.event?.choices?.length > 0 ? response.event.choices : [""]);
          setFtoCount(response.event.ftoCount ? response.event.ftoCount : "");
          setInitialFTOCount(response.event.ftoCount ? response.event.ftoCount : "");
          setLastUpdatedByFirstName(response.event?.modifiedByFirstName);
          setLastUpdatedByLastName(response.event?.modifiedByLastName);
          setIsPageLoading(false);
          setIsBidChoicesLoading(false);
          if (bidChoiceEvent.status === BID_STATUS.CLOSED) {
            setSelectedChoices(response.event?.choices?.length > 0 ? response.event.choices : []);
            setIsReadOnly(true);
          }
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
        setIsPageLoading(false);
      });
  };

  const handleSubmitClicked = () => {
    setIsSubmitLoading(true);
    console.log("Saving bid choice data...");
    let payload = buildBidChoiceEvent(
      prepareNewBidChoiceEventPayload({ bidChoiceData, selectedChoices, ftoCount }),
      userSession
    );
    addBidChoiceEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        if (result.message) {
          console.error("Error: ", result.message);
          setSubmitErrors(result.message);
          setIsSubmitLoading(false);
          setIsSubmitDialogOpen(false);
          setIsSubmitResponseDialogOpen(true);
        } else {
          console.log("Success!");
          setIsSubmitDialogOpen(false);
          setIsSubmitLoading(false);
          setIsSubmitResponseDialogOpen(true);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsSubmitDialogOpen(false);
        setIsSubmitLoading(false);
      });
  };

  const validateForm = () => {
    let isFormValid = false;
    const numOfChoices = getNonEmptyChoices(selectedChoices)?.length;
    const errors = validateFTOCount(ftoCount, numOfChoices);
    setFtoCountErrors(errors);

    if (errors === "") {
      isFormValid = true;
    }

    return isFormValid;
  };

  const handleClickSubmitDialogOpen = () => {
    if (validateForm()) {
      setSelectedChoices(getNonEmptyChoices(selectedChoices));
      setIsSubmitDialogOpen(true);
    }
  };

  const handleClickSubmitDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSubmitDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    if (
      !isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) ||
      !isEqual(ftoCount, initialFTOCount)
    ) {
      setIsExitDialogOpen(true);
    } else {
      // no changes made, so re-direct to my bids
      history.push(getMyBidsPath());
    }
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const handleClickGuideDialogOpen = () => {
    setIsGuideDialogOpen(true);
  };

  const handleCloseClickedGuideDialog = () => {
    setIsGuideDialogOpen(false);
  };

  const handleClickClearAllDialogOpen = () => {
    setIsClearAllDialogOpen(true);
  };

  const handleClickCancelClearAllDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsClearAllDialogOpen(false);
  };

  const handleClearAllChoices = () => {
    setSelectedChoices([""]);
    setIsClearAllDialogOpen(false);
  };

  const getLastUpdatedTimeString = () => {
    return getStationTimeZoneDateTimeStringFromUTCInteger(getLastSubmittedTimestamp(), bidChoiceData.station).toFormat(
      "MM/dd/yyyy T ZZZZ"
    );
  };

  const getMyBidsPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.MY_BIDS).path;
  };

  const getLastSubmittedTimestamp = () => {
    return bidChoiceData?.createdAt ? Date.parse(bidChoiceData.createdAt) : "";
  };

  const renderDesktopNavBtnsForBidForm = () => {
    return (
      <>
        {isReadOnly ? (
          <>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <IAMFTOBidPDFExport bidChoiceData={bidChoiceData} buttonVariant="outlined" />
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                }}
                variant="contained"
                color="primary"
                onClick={() =>
                  history.push({
                    pathname: getMyBidsPath(),
                  })
                }
              >
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
                Cancel
              </Button>
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <IAMFTOBidPDFExport bidChoiceData={bidChoiceData} buttonVariant="contained" />
            </Box>
            <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                  backgroundColor: "secondary.swaGray",
                  "&:hover": {
                    backgroundColor: "secondary.swaGray5",
                  },
                }}
                variant="contained"
                onClick={handleClickGuideDialogOpen}
              >
                Guide
              </Button>
            </Box>
            <Box sx={{ paddingTop: "10px" }}>
              <Button
                sx={{ width: "100px" }}
                variant="contained"
                color="primary"
                onClick={handleClickSubmitDialogOpen}
                disabled={
                  bidChoiceData.status !== EMPLOYEE_BID_STATUS.OPEN ||
                  (isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) &&
                    isEqual(ftoCount, initialFTOCount))
                }
              >
                Submit
              </Button>
            </Box>
          </>
        )}
      </>
    );
  };

  const handleAddChoice = () => {
    const existingChoices = [...selectedChoices];
    setSelectedChoices([...existingChoices, ""]);
    setTimeout(() => {
      const rowElem = document.querySelector(`[data-id="${selectedChoices.length + 1}"]`);
      if (rowElem) {
        rowElem.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 0);
  };

  const renderSubmitFTOBidChoiceDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        sx={{ margin: "auto" }}
        open={isSubmitDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
      >
        {selectedChoices?.length === 0 ? (
          <>
            <DialogTitle>Submit a Blank Bid</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>You are submitting a blank bid.</div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You can continue to submit choices up until the bid window closes.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
          </>
        ) : (
          <>
            <DialogTitle>Submit Bid Choices</DialogTitle>
            <DialogContent>
              <DialogContentText component={"div"}>
                <Grid container>
                  <div style={{ marginTop: "10px" }}>
                    You are submitting{" "}
                    <b>
                      {selectedChoices?.length} choice{selectedChoices?.length > 1 && "s"}
                    </b>
                    {" for "}
                    <b>
                      {ftoCount} FTO{ftoCount > 1 && "s"}
                    </b>
                    .
                  </div>
                  <div style={{ marginTop: "10px" }}>Any choices previously submitted will be overriden.</div>
                  <div style={{ marginTop: "10px" }}>
                    You will see your submitted choices the next time you open this bid form again. You can keep editing
                    your choices until the bid window closes.
                  </div>
                </Grid>
              </DialogContentText>
            </DialogContent>
          </>
        )}
        <DialogActions>
          <ConfirmButton variant="outlined" color="primary" onClick={handleClickSubmitDialogClose}>
            Cancel
          </ConfirmButton>
          <ConfirmButton variant="contained" color="primary" onClick={handleSubmitClicked} isLoading={isSubmitLoading}>
            Submit
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const renderBidChoices = () => {
    return (
      <>
        <Grid
          container
          sx={{
            border: "solid 1px",
            borderColor: "secondary.swaGray2",
            backgroundColor: "secondary.swaGray2",
            paddingLeft: "1rem",
          }}
        >
          <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {getNonEmptyChoices(selectedChoices).length}
              {` Choice${getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}`} Entered
            </Typography>
          </Grid>

          <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
            <Button sx={{ fontWeight: "bold", textDecoration: "underline" }} onClick={handleClickClearAllDialogOpen}>
              Clear All
            </Button>
            <ConfirmationDialog
              isDialogOpen={isClearAllDialogOpen}
              title={BID_FORM_CLEAR_ALL}
              message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
              handleNoButtonClicked={handleClickCancelClearAllDialogClose}
              handleYesButtonClicked={handleClearAllChoices}
              noTitle="Cancel"
              yesTitle="Clear All"
            />
          </Grid>
        </Grid>

        <Grid container>
          <Box sx={{ width: "inherit" }}>
            {
              <BidChoicesTable
                selectedChoices={selectedChoices}
                setSelectedChoices={setSelectedChoices}
                isDesktopView={true}
                isBidChoicesLoading={isBidChoicesLoading}
                bidChoiceData={bidChoiceData}
                disabledDates={disabledDates}
                bidType={bidChoiceData.bidType}
              />
            }
            <Divider />
            <Button
              className={classes.addChoice}
              variant="contained"
              onClick={handleAddChoice}
              disabled={selectedChoices.some((choice) => choice === "") ? true : false}
            >
              Add Another Choice
            </Button>
          </Box>
        </Grid>
      </>
    );
  };

  const renderMobileNavBtnsForBidForm = () => {
    return (
      <>
        {isReadOnly ? (
          <Grid container justifyContent="space-between" direction="row" alignItems="center" sx={{ margin: "5px" }}>
            <Grid item xs={4} sx={{ padding: 1, display: "flex", justifyContent: "start" }}>
              <Box sx={{ paddingTop: "10px" }}>
                <IAMFTOBidPDFExport bidChoiceData={bidChoiceData} buttonVariant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
              <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                <Button
                  sx={{
                    width: "100px",
                  }}
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    history.push({
                      pathname: getMyBidsPath(),
                    })
                  }
                >
                  Close
                </Button>
              </Box>
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid item xs={3} sx={{ padding: 1, display: "flex", justifyContent: "start", marginLeft: "10px" }}>
              <Button
                sx={{
                  width: "100px",
                  backgroundColor: "secondary.swaGray",
                  "&:hover": {
                    backgroundColor: "secondary.swaGray5",
                  },
                }}
                variant="contained"
                onClick={handleClickGuideDialogOpen}
              >
                Guide
              </Button>
            </Grid>
            <Grid item xs={8} sx={{ padding: 1, display: "flex", justifyContent: "end" }}>
              <Button sx={{ width: "100px" }} variant="outlined" color="primary" onClick={handleClickCancelDialogOpen}>
                Cancel
              </Button>
              <Button
                sx={{ width: "100px", marginLeft: "10px", marginRight: "-10px" }}
                variant="contained"
                color="primary"
                onClick={handleClickSubmitDialogOpen}
                disabled={
                  bidChoiceData.status !== EMPLOYEE_BID_STATUS.OPEN ||
                  (isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) &&
                    isEqual(ftoCount, initialFTOCount))
                }
              >
                Submit
              </Button>
            </Grid>
          </>
        )}
      </>
    );
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_BIDS, getBidName(bidChoiceData)];
  const breadcrumbsLinks = [HOME_PATH, `/${FEATURES.MY_BIDS}`];
  const renderDesktopFtoBidChoicesPage = () => {
    if (isPageLoading || isScheduleLoading) {
      return <PageLoading />;
    }
    return (
      <>
        <StarsBreadcrumbs
          breadcrumbsList={breadcrumbsList}
          breadcrumbsLinks={breadcrumbsLinks}
          isConfirmationRequired={
            !isEqual(getNonEmptyChoices(selectedChoices), getNonEmptyChoices(initialSelectedChoices)) ||
            !isEqual(ftoCount, initialFTOCount)
          }
          cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
        />
        {/* Title & Cancel/Submit Buttons */}
        <Grid container>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ paddingLeft: "2rem", paddingTop: "10px" }}>
              <Grid item>
                <Typography variant="h5" component="div" align="left">
                  {getBidName(bidChoiceData)} FTO Bid
                </Typography>

                {getLastSubmittedTimestamp() !== "" &&
                lastUpdatedByFirstName &&
                lastUpdatedByLastName &&
                lastUpdatedByFirstName !== "" &&
                lastUpdatedByLastName !== "" ? (
                  <Grid className={classes.lastSubmitted}>
                    {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()}`}
                  </Grid>
                ) : getLastSubmittedTimestamp() !== "" ? (
                  <Grid
                    className={classes.lastSubmitted}
                  >{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()}`}</Grid>
                ) : (
                  ""
                )}

                <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                  {bidChoiceData && bidChoiceData.windowStartDate !== null && bidChoiceData.windowEndDate !== null && (
                    <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                      <BidTimer
                        windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                        station={bidChoiceData.station}
                      />
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Stack>
              <Grid item sx={{ display: "flex", paddingRight: "2rem" }}>
                {renderDesktopNavBtnsForBidForm()}
              </Grid>
            </Stack>
          </Grid>
          <Box
            sx={{
              width: "100%",
              marginLeft: "2rem",
              marginRight: "2rem",
              marginTop: "1rem",
              border: "solid 1px",
              borderColor: "secondary.swaGray2",
            }}
          >
            {isReadOnly ? (
              <>
                <Grid
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "1rem",
                    textAlign: "left",
                  }}
                >
                  <Grid sx={{ height: "40px", alignContent: "center" }}>
                    <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                      {`${ftoCount || 0} FTO${parseInt(ftoCount) > 1 ? "s" : ""} Requested & ${
                        selectedChoices ? selectedChoices.length : 0
                      } Choice${selectedChoices?.length > 1 ? "s" : ""} Entered`}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                  }}
                >
                  <Box
                    sx={{
                      height: `calc(85vh - 200px)`,
                      width: "inherit",
                      "& .choices.selected": { backgroundColor: "secondary.swaLightestBlue" },
                      "& .add-remove-icons.blocked": { backgroundColor: "secondary.swaGray2" },
                      "& .add-remove-icons.addIcon": { backgroundColor: "secondary.swaBlue" },
                    }}
                  >
                    <BidChoicesTable
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={true}
                      isBidChoicesLoading={isBidChoicesLoading}
                      bidChoiceData={bidChoiceData}
                      isReadOnly={true}
                      disabledDates={disabledDates}
                      bidType={bidChoiceData.bidType}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Grid container sx={{ paddingTop: "10px" }}>
                  <Grid
                    item
                    sx={{
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      fontSize: "14px",
                      textAlign: "left",
                      marginTop: "15px",
                    }}
                  >
                    Enter the # of FTOs you would like to use (0-10):
                  </Grid>
                  <Grid item>
                    <div className={classes.bidInput}>
                      <TextField
                        type="text"
                        data-testid="choices-count-textField"
                        className={classes.inputText}
                        variant="outlined"
                        value={ftoCount}
                        onChange={handleFTOCountChange}
                        onBlur={handleFTOCountBlur}
                        onKeyUp={handleFTOCountEnterKeyPressed}
                        inputProps={{ inputMode: "numeric", maxLength: 2 }}
                        error={ftoCountErrors !== ""}
                        helperText={ftoCountErrors}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    width: "100%",
                    margin: "auto",
                    marginTop: "10px",
                  }}
                >
                  {renderBidChoices()}
                </Box>
              </>
            )}
          </Box>
        </Grid>
        {renderDialogs()}
      </>
    );
  };

  const renderMobileFtoBidChoicesPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    }
    return (
      <>
        {/* Title */}
        <Grid container>
          <Grid container justifyContent="space-between">
            <Grid item sx={{ paddingLeft: "1rem", paddingTop: "20px" }}>
              <Typography variant="h5" component="div" align="left">
                {getBidName(bidChoiceData)} FTO Bid
              </Typography>

              {getLastSubmittedTimestamp() !== "" &&
              lastUpdatedByFirstName &&
              lastUpdatedByLastName &&
              lastUpdatedByFirstName !== "" &&
              lastUpdatedByLastName !== "" ? (
                <Grid className={classes.lastSubmitted}>
                  {`${LAST_UPDATED}${lastUpdatedByLastName}, ${lastUpdatedByFirstName} ${getLastUpdatedTimeString()}`}
                </Grid>
              ) : getLastSubmittedTimestamp() !== "" ? (
                <Grid
                  className={classes.lastSubmitted}
                >{`${LAST_UPDATED_TIMESTAMP} ${getLastUpdatedTimeString()}`}</Grid>
              ) : (
                ""
              )}

              <Box sx={{ textAlign: "left", paddingTop: "10px" }}>
                {bidChoiceData && bidChoiceData.windowStartDate !== null && bidChoiceData.windowEndDate !== null && (
                  <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "12px" }}>
                    <Grid container spacing={0.5}>
                      <Grid item>
                        <AccessTimeIcon fontSize="12px" />
                      </Grid>
                      <Grid item xs={11.2} sm={11.2} style={{ fontWeight: "bold", fontSize: "10px" }}>
                        <BidTimer
                          windowDates={[bidChoiceData.windowStartDate, bidChoiceData.windowEndDate]}
                          station={bidChoiceData.station}
                        />
                      </Grid>
                    </Grid>
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ width: "100%", margin: "auto", marginTop: "10px" }}>
            {isReadOnly ? (
              <>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaLightestBlue",
                    paddingLeft: "1rem",
                    textAlign: "left",
                  }}
                >
                  <Grid>
                    <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
                      {`${ftoCount || 0} FTO${parseInt(ftoCount) > 1 ? "s" : ""} Requested & ${
                        selectedChoices ? selectedChoices.length : 0
                      } Choice${selectedChoices?.length > 1 ? "s" : ""} Entered`}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                  }}
                >
                  <Box sx={{ width: "inherit" }}>
                    <BidChoicesTable
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={false}
                      isBidChoicesLoading={isBidChoicesLoading}
                      bidChoiceData={bidChoiceData}
                      isReadOnly={true}
                      disabledDates={disabledDates}
                      bidType={bidChoiceData.bidType}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              <>
                <Divider />
                <Grid container sx={{ paddingTop: "10px" }}>
                  <Grid
                    item
                    xs={7.5}
                    sx={{
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      fontSize: "14px",
                      textAlign: "left",
                      marginTop: "10px",
                    }}
                  >
                    Enter the # of FTOs you would like to use (0-10):
                  </Grid>
                  <Grid item xs={4.5} sx={{ display: "flex" }}>
                    <div className={classes.bidInput}>
                      <TextField
                        type="text"
                        data-testid="choices-count-textField"
                        className={classes.inputText}
                        variant="outlined"
                        value={ftoCount}
                        onChange={handleFTOCountChange}
                        onBlur={handleFTOCountBlur}
                        onKeyUp={handleFTOCountEnterKeyPressed}
                        inputProps={{ inputMode: "numeric", maxLength: 2 }}
                        error={ftoCountErrors !== ""}
                        helperText={ftoCountErrors}
                      />
                    </div>
                  </Grid>
                </Grid>
                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    backgroundColor: "secondary.swaGray2",
                    paddingLeft: "1rem",
                    marginTop: 1,
                  }}
                >
                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "start", paddingTop: "6px" }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {getNonEmptyChoices(selectedChoices).length}
                      {` Choice${getNonEmptyChoices(selectedChoices).length > 1 ? "s" : ""}`} Entered
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "8px" }}>
                    <Button
                      sx={{ fontWeight: "bold", textDecoration: "underline" }}
                      onClick={handleClickClearAllDialogOpen}
                    >
                      Clear All
                    </Button>
                    <ConfirmationDialog
                      isDialogOpen={isClearAllDialogOpen}
                      title={BID_FORM_CLEAR_ALL}
                      message={BID_FORM_CLEAR_ALL_CHOICES_MESSAGE}
                      handleNoButtonClicked={handleClickCancelClearAllDialogClose}
                      handleYesButtonClicked={handleClearAllChoices}
                      noTitle="Cancel"
                      yesTitle="Clear All"
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  sx={{
                    border: "solid 1px",
                    borderColor: "secondary.swaGray2",
                    borderBottom: 0,
                  }}
                >
                  <Box sx={{ width: "inherit" }}>
                    <BidChoicesTable
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      isDesktopView={false}
                      isBidChoicesLoading={isBidChoicesLoading}
                      bidChoiceData={bidChoiceData}
                      disabledDates={disabledDates}
                      bidType={bidChoiceData.bidType}
                    />
                  </Box>
                </Grid>
                <Divider />
                <Button
                  className={classes.addChoice}
                  variant="contained"
                  onClick={handleAddChoice}
                  disabled={selectedChoices.some((choice) => choice === "") ? true : false}
                >
                  Add Another Choice
                </Button>
                <Divider />
              </>
            )}
          </Box>

          <Grid
            container
            sx={{
              marginTop: "5px",
              bottom: 0,
              position: "fixed",
              backgroundColor: "secondary.swaWhite",
              zIndex: 999,
            }}
          >
            {renderMobileNavBtnsForBidForm()}
          </Grid>
        </Grid>
        {renderDialogs()}
      </>
    );
  };

  const renderDialogs = () => {
    return (
      <>
        <ConfirmationDialog
          isDialogOpen={isExitDialogOpen}
          title="Cancel"
          message={BID_FORM_CANCEL_MESSAGE}
          handleNoButtonClicked={handleClickCancelDialogClose}
          redirectTo={getMyBidsPath()}
        />
        {renderSubmitFTOBidChoiceDialog()}
        <BidInfoDialog
          isDialogOpen={isGuideDialogOpen}
          title="Guide"
          handleCloseClicked={handleCloseClickedGuideDialog}
          isDesktopView={props.isDesktopView}
          eid={userSession.eid}
          bidChoiceData={bidChoiceData}
          lastSubmittedTimestamp={getLastSubmittedTimestamp()}
          bidType={bidChoiceData.bidType}
        />
        <SuccessDialog
          isSuccessDialogOpen={isSubmitResponseDialogOpen}
          title={FTO_BID_CHOICE_CONFIRMATION_TITLE}
          message={FTO_BID_CHOICE_CONFIRMATION_MESSAGE}
          hasError={submitErrors !== ""}
          error={BID_CHOICE_SAVE_ERROR_MESSAGE}
          redirectTo={submitErrors === "" && getMyBidsPath()}
          handleOKClick={() => setIsSubmitResponseDialogOpen(false)}
        />
      </>
    );
  };

  return (
    <div className="App-contents enable-scroll" data-testid="fto-bid-choices-page">
      {props.isDesktopView ? renderDesktopFtoBidChoicesPage() : renderMobileFtoBidChoicesPage()}
    </div>
  );
};

export default IAMFTOBidChoices;
